import { useState, useEffect } from "react";
import { apiGet } from "../lib/api";
import { BRAND } from "../lib/brand";
import { SartellLogo } from "../components/Logo";

/* ───────────────────── PLAYER NAME SCREEN ───────────────────── */
export function PlayerNameScreen({ team, token, onContinue, onBack }) {
  const [playerName, setPlayerName] = useState("");
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    apiGet("/roster/" + team.id, token)
      .then((data) => {
        setRoster(data.players || []);
        setLoadingRoster(false);
      })
      .catch(() => setLoadingRoster(false));
  }, [team.id, token]);

  const hasRoster = roster.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8"><SartellLogo size="lg" /></div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <button onClick={onBack} className="text-sm hover:text-blue-600 mb-4 flex items-center gap-1" style={{ color: BRAND.accent }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <h2 className="text-xl font-bold text-gray-800 text-center mb-1">Select Player</h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            {hasRoster ? "Choose your player from the roster" : "Enter your player's name"} for {team.name}
          </p>

          {loadingRoster ? (
            <p className="text-center text-gray-400 py-4">Loading roster...</p>
          ) : hasRoster && !useCustom ? (
            <>
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                {/* Coaches and staff sit on the same roster and mark dates the
                    same way — they just belong under their own heading so a
                    coach can find themselves without hunting through the kids. */}
                {[
                  { role: "player", label: "Players" },
                  { role: "staff", label: "Coaches & staff" },
                ].map(({ role, label }) => {
                  const group = roster.filter((p) => (p.role || "player") === role);
                  if (group.length === 0) return null;
                  const showHeading = roster.some((p) => (p.role || "player") === "staff");
                  return (
                    <div key={role} className="space-y-2">
                      {showHeading && (
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 pt-1">{label}</p>
                      )}
                      {group.map((p) => (
                        <button
                          key={p.name}
                          onClick={() => setPlayerName(p.name)}
                          className={"w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left " +
                            (playerName === p.name
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50")}
                        >
                          <div className={"w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold " +
                            (playerName === p.name ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500")}>
                            {p.jerseyNumber || p.name.charAt(0)}
                          </div>
                          <span className={"text-sm font-medium " + (playerName === p.name ? "text-blue-800" : "text-gray-700")}>
                            {p.name}
                          </span>
                          {playerName === p.name && (
                            <svg className="w-5 h-5 text-blue-600 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
              <button onClick={() => setUseCustom(true)}
                className="w-full text-xs text-gray-400 hover:text-blue-600 mb-4 transition-colors">
                Player not listed? Enter name manually
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                placeholder="e.g. Alex Johnson"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && playerName.trim() && onContinue(playerName.trim())}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent mb-4"
                autoFocus
              />
              {hasRoster && (
                <button onClick={() => { setUseCustom(false); setPlayerName(""); }}
                  className="w-full text-xs text-gray-400 hover:text-blue-600 mb-4 transition-colors">
                  Back to roster list
                </button>
              )}
            </>
          )}

          <button
            onClick={() => playerName.trim() && onContinue(playerName.trim())}
            disabled={!playerName.trim()}
            className="w-full py-3 text-white font-semibold rounded-xl transition-colors shadow-sm disabled:opacity-50"
            style={{ backgroundColor: BRAND.primary }}
          >
            Mark Availability
          </button>
        </div>
      </div>
    </div>
  );
}
